import { useEffect, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { cn } from '@r/utils/utils.ts';

const COPIED_FEEDBACK_DURATION = 2000;

interface CopyButtonProps {
  value: string;
  className?: string;
}

/*
 * Bouton icône qui copie une valeur dans le presse-papier, avec un retour visuel temporaire.
 */
export const CopyButton = ({ value, className }: CopyButtonProps) => {
  const { t } = useTranslation();
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    if (!isCopied) return;

    const timeout = setTimeout(() => setIsCopied(false), COPIED_FEEDBACK_DURATION);
    return () => clearTimeout(timeout);
  }, [isCopied]);

  const handleCopy = () => {
    navigator.clipboard
      .writeText(value)
      .then(() => {
        setIsCopied(true);
        toast.success(t('common.clipboard.success'));
      })
      .catch(() => toast.error(t('common.clipboard.error')));
  };

  const Icon = isCopied ? Check : Copy;

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={t('common.clipboard.tooltip')}
      className={cn(
        'shrink-0 text-muted-foreground hover:text-foreground transition-colors',
        className
      )}
    >
      <Icon className={cn('w-4 h-4', isCopied && 'text-emerald-600')} />
    </button>
  );
};
